import EventEmitter from "events";
import {
  UserManager,
  type User,
  type UserManagerSettings,
} from "oidc-client-ts";
import { AUTH_CONFIG } from "../config/legacy-frozen-config";
import type { AuthStrategy, Token } from "./types";

let userManager: UserManager | undefined;
let user: User | null = null;

let initP: Promise<boolean> | undefined;

// Need to have some sort of event dispatching since the keycloak sdk only
// allows you to set one function for each event, but you might want to use the hook in multiple places.
let eventSource: EventEmitter | undefined;
let interval: number | null = null;

const getOidcConfig = () => {
  if (AUTH_CONFIG.type !== "oidc") {
    throw Error(
      "Oidc auth function was called but AUTH_CONFIG type is not oidc "
    );
  }
  return AUTH_CONFIG;
};

const getUserManagerInstance = () => {
  if (!userManager) {
    const oidcConfig = getOidcConfig();
    if (!oidcConfig.url || !oidcConfig.aud) {
      throw new Error(
        "Cannot retrieve OIDC instance: Auth URL or audience not set."
      );
    }

    const oidcInitOptions: UserManagerSettings = {
      authority: oidcConfig.url,
      client_id: "B3L3BDeJ29mvYROIl5SXyZlId7OHTqdF",
      redirect_uri: location.origin,
      revokeTokensOnSignout: true,
      includeIdTokenInSilentSignout: true,
      scope: "openid email",
      extraQueryParams: {
        audience: oidcConfig.aud,
      },
    };

    try {
      userManager = new UserManager(oidcInitOptions);
    } catch (error) {
      console.error("getUserManager_failed: ", error);
      throw Error(`getUserManager_failed: ${JSON.stringify(error)}`);
    }
  }
  return userManager;
};

const getEventEmitterInstance = () => {
  if (!eventSource) {
    eventSource = new EventEmitter();
  }
  return eventSource;
};

const setupOidcEvents = () => {
  getEventEmitterInstance().on("onAuthLogout", () => {
    if (interval !== null) {
      window.clearInterval(interval);
      interval = null;
      getUserManagerInstance().signoutRedirect();
    }
  });

  getEventEmitterInstance().on("onTokenExpired", () => {
    getUserManagerInstance()
      .signinSilent()
      .catch(() => {
        console.error("Could not refresh the token, logging out.");
        getUserManagerInstance().signoutSilent();
      });
  });
  getUserManagerInstance().events.addUserSessionChanged(() =>
    handleTokenEvent("onActionUpdate")()
  );
  getUserManagerInstance().events.addUserSignedIn(() =>
    handleTokenEvent("onAuthSuccess")()
  );
  getUserManagerInstance().events.addUserSignedOut(() =>
    handleTokenEvent("onAuthLogout")()
  );
  getUserManagerInstance().events.addAccessTokenExpired(() =>
    handleTokenEvent("onTokenExpired")()
  );
};

export const initOidc: AuthStrategy["init"] = () => {
  setupOidcEvents();

  const userManager = getUserManagerInstance();

  initP = (async () => {
    try {
      const query = window.location.search;
      if (query.includes("code=") && query.includes("state=")) {
        await userManager.signinRedirectCallback();
        /** Trigger hard reload once the session is created */
        location.assign("/");
        return true;
      }
      user = await userManager.getUser();

      if (user?.access_token) {
        return true;
      } else {
        await userManager.signinRedirect({
          redirect_uri: location.origin,
          scope: userManager.settings.scope,
        });
      }
    } catch (error) {
      console.error("Oidc init error:", error);
    }
    return false;
  })();
};

const handleTokenEvent = (type: string) => () => {
  getEventEmitterInstance().emit(type, getToken());
};

export const onTokenUpdate: AuthStrategy["onTokenUpdate"] = (
  callback: (token: Token) => void
) => {
  getEventEmitterInstance().on("onActionUpdate", callback);
  getEventEmitterInstance().on("onAuthSuccess", callback);
  getEventEmitterInstance().on("onAuthLogout", callback);
  getEventEmitterInstance().on("onTokenExpired", callback);

  return () => {
    getEventEmitterInstance().off("onActionUpdate", callback);
    getEventEmitterInstance().off("onAuthSuccess", callback);
    getEventEmitterInstance().off("onAuthLogout", callback);
    getEventEmitterInstance().off("onTokenExpired", callback);
  };
};

// Placeholder which we will replace in all calls to fetch and XMLHttpRequest.
// It must be url-safe and also quite unique. So, quoting RFC3986
// (https://www.ietf.org/rfc/rfc3986.txt)
//
// 2.3.  Unreserved Characters
//
//   Characters that are allowed in a URI but do not have a reserved
//   purpose are called unreserved.  These include uppercase and lowercase
//   letters, decimal digits, hyphen, period, underscore, and tilde.
//
//      unreserved  = ALPHA / DIGIT / "-" / "." / "_" / "~"
//
// Leaves us with the characters we can use safely.
//
// We also exclude "." to be regex safe.
//
const symbolicToken = "___---TOKEN-PLACEHOLDER---___";

export const login: AuthStrategy["login"] = (param) =>
  getUserManagerInstance().signinRedirect(
    param?.redirectUri ? { redirect_uri: param?.redirectUri } : {}
  );
export const logout: AuthStrategy["logout"] = () =>
  getUserManagerInstance()
    .signoutRedirect
    // TODO: param?.redirectUri ? { post_logout_redirect_uri: param?.redirectUri } : {}
    ();

function parseJwt(token: string): Record<string, unknown> {
  const base64Url = token.split(".")[1];
  if (base64Url) {
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  }

  return {};
}

export const getToken: AuthStrategy["getToken"] = () =>
  user?.access_token
    ? {
        token: symbolicToken,
        parsedToken: parseJwt(user.access_token),
      }
    : null;

export const getTokenP: AuthStrategy["getTokenP"] = (): Promise<
  NonNullable<ReturnType<typeof getToken>>
> =>
  (
    initP ??
    Promise.reject(new Error("You need to call initKeycloak before getTokenP"))
  ).then((isAuthenticated) => {
    if (!isAuthenticated) {
      throw new Error("Not authenticated");
    }
    const token = getToken();
    if (!token) {
      throw new Error("Couldn't retrieve token");
    }
    return token;
  });

// Monkey patch code where the token is used to replace fake token with real.
// These are the things patched:
//
// * fetch
// * XMLHttpRequest
// * window.open
// * WebSocket
//
export const patchRequests: AuthStrategy["patchRequests"] = () => {
  const shouldReplaceToken = (url: Request | URL | string) => {
    try {
      const hostname = (
        url instanceof URL
          ? url
          : new URL(url instanceof Request ? url.url : url)
      ).hostname;

      return (
        hostname.endsWith(".sinch.se") ||
        hostname.endsWith(".sinch.com") ||
        hostname.endsWith(".messengerpeople.com") ||
        hostname.endsWith(".chatlayer.ai") ||
        hostname.endsWith(".wbcsrv.com") ||
        hostname == "localhost"
      );
    } catch (error) {
      console.error("shouldReplaceToken_failed: ", error);
      return false;
    }
  };

  const placeholderRegEx = new RegExp(symbolicToken, "g");
  const replaceToken = (value: string) =>
    value?.replace?.(placeholderRegEx, user?.access_token ?? "N/A") ?? value;

  //////////////////////////////////////////////////////////////////////////////
  // Patch fetch
  const { fetch: originalFetch } = window;
  window.fetch = (...args) => {
    const patchedRequest = new Request(...args);
    const replace = shouldReplaceToken(patchedRequest.url);

    patchedRequest.headers.forEach((value, name) => {
      patchedRequest.headers.set(name, replace ? replaceToken(value) : value);
    });

    return originalFetch(patchedRequest);
  };

  //////////////////////////////////////////////////////////////////////////////
  // Patch XMLHttpRequest
  const { setRequestHeader } = XMLHttpRequest.prototype;
  XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
    // TODO: We should only replace the token if the request is to our own APIs.
    // Perhaps do it the same way as we do for the x-project-id header.
    // eslint-disable-next-line no-constant-condition
    if (true) {
      return setRequestHeader.call(this, name, replaceToken(value));
    }
    return setRequestHeader.call(this, name, value);
  };
  //////////////////////////////////////////////////////////////////////////////
  // Patch window.open
  // Really shouldn't send tokens in URL. Code doing this should be refactored
  // at some point...
  const windowOpen = window.open;
  window.open = (url, ...rest) =>
    windowOpen(
      url && shouldReplaceToken(url)
        ? replaceToken(new URL(url, location.href).href)
        : url,
      ...rest
    );

  //////////////////////////////////////////////////////////////////////////////
  // Patch WebSocket
  const { send } = WebSocket.prototype;
  WebSocket.prototype.send = function (data) {
    return send.call(this, typeof data == "string" ? replaceToken(data) : data);
  };

  // reset to original
  return () => {
    window.open = windowOpen;
    window.fetch = originalFetch;
    XMLHttpRequest.prototype.setRequestHeader = setRequestHeader;
    WebSocket.prototype.send = send;
  };
};
